import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

/**
 * SectionCta — reusable conversion banner placed between sections.
 * variant:
 *   "soft"   — beige rounded card with button (most common)
 *   "dark"   — full-bleed dark band, white pill button
 *   "inline" — single-line strip with arrow link
 */
export const SectionCta = ({
    variant = "soft",
    eyebrowKey,
    titleKey,
    subtitleKey,
    ctaKey,
    testId = "section-cta",
}) => {
    const { t } = useTranslation();

    const eyebrow = eyebrowKey ? t(eyebrowKey) : null;
    const title = t(titleKey);
    const subtitle = subtitleKey ? t(subtitleKey) : null;
    const cta = t(ctaKey);

    if (variant === "inline") {
        return (
            <div
                data-testid={testId}
                className="py-10 md:py-14 bg-white border-y border-stone-200/70"
            >
                <div className="max-w-5xl mx-auto px-6 md:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <p className="font-serif text-xl md:text-2xl text-stone-900 leading-snug text-center sm:text-left">
                        {title}
                    </p>
                    <button
                        onClick={() => scrollTo("#quiz")}
                        className="group inline-flex items-center gap-2 text-stone-900 hover:text-gold-deep transition-colors whitespace-nowrap"
                        data-testid={`${testId}-link`}
                    >
                        <span className="link-underline text-sm uppercase tracking-[0.18em]">
                            {cta}
                        </span>
                        <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                    </button>
                </div>
            </div>
        );
    }

    if (variant === "dark") {
        return (
            <section
                data-testid={testId}
                className="relative py-20 md:py-28 bg-stone-900 text-stone-50 overflow-hidden"
            >
                <div className="absolute inset-0 bg-grain opacity-25 pointer-events-none" />
                <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="relative max-w-3xl mx-auto px-6 md:px-12 text-center"
                >
                    {eyebrow && (
                        <div className="flex items-center justify-center gap-3 mb-5">
                            <span className="inline-block w-10 h-px bg-gold" />
                            <span className="text-xs uppercase tracking-[0.24em] text-stone-400">
                                {eyebrow}
                            </span>
                            <span className="inline-block w-10 h-px bg-gold" />
                        </div>
                    )}
                    <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-[1.1] tracking-tight">
                        {title}
                    </h2>
                    {subtitle && (
                        <p className="mt-5 text-stone-300/90 font-light text-base md:text-lg leading-relaxed max-w-xl mx-auto">
                            {subtitle}
                        </p>
                    )}
                    <Button
                        onClick={() => scrollTo("#quiz")}
                        className="mt-9 rounded-full bg-stone-50 hover:bg-white text-stone-900 px-8 py-6 text-base h-auto group"
                        data-testid={`${testId}-button`}
                    >
                        {cta}
                        <ArrowRight size={18} className="ml-2 transition-transform group-hover:translate-x-1" />
                    </Button>
                </motion.div>
            </section>
        );
    }

    // "soft" — default beige card variant
    return (
        <section
            data-testid={testId}
            className="py-16 md:py-20"
        >
            <div className="max-w-5xl mx-auto px-6 md:px-12">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="relative bg-beige-50 rounded-3xl ring-1 ring-stone-200 overflow-hidden px-7 py-10 md:px-14 md:py-14"
                >
                    {/* Decorative gold sparkle */}
                    <Sparkles
                        size={20}
                        className="text-gold absolute top-6 right-6 opacity-70"
                        aria-hidden="true"
                    />
                    <div className="grid md:grid-cols-12 gap-8 items-center">
                        <div className="md:col-span-8">
                            {eyebrow && (
                                <div className="flex items-center gap-3 mb-4">
                                    <span className="gold-divider" />
                                    <span className="text-xs uppercase tracking-[0.24em] text-stone-500">
                                        {eyebrow}
                                    </span>
                                </div>
                            )}
                            <h3 className="font-serif text-3xl sm:text-4xl text-stone-900 leading-[1.1] tracking-tight">
                                {title}
                            </h3>
                            {subtitle && (
                                <p className="mt-4 text-stone-600 font-light leading-relaxed max-w-xl">
                                    {subtitle}
                                </p>
                            )}
                        </div>
                        <div className="md:col-span-4 md:text-right">
                            <Button
                                onClick={() => scrollTo("#quiz")}
                                className="rounded-full bg-stone-900 hover:bg-stone-800 text-stone-50 px-7 py-6 text-base h-auto group"
                                data-testid={`${testId}-button`}
                            >
                                {cta}
                                <ArrowRight size={18} className="ml-2 transition-transform group-hover:translate-x-1" />
                            </Button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};
